import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindAllProductPageComponentsQueryResponse,
  FindAllProductPageComponentsQueryParams,
} from "../types/FindAllProductPageComponents"

type FindAllProductPageComponentsClient = typeof client<
  FindAllProductPageComponentsQueryResponse,
  Error,
  never
>
type FindAllProductPageComponents = {
  data: FindAllProductPageComponentsQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindAllProductPageComponentsQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindAllProductPageComponentsClient>>
  client: {
    parameters: Partial<Parameters<FindAllProductPageComponentsClient>[0]>
    return: Awaited<ReturnType<FindAllProductPageComponentsClient>>
  }
}
export const findAllProductPageComponentsQueryKey = (
  params: FindAllProductPageComponents["queryParams"]
) =>
  [{ url: "/v1/product-page-components" }, ...(params ? [params] : [])] as const
export type FindAllProductPageComponentsQueryKey = ReturnType<
  typeof findAllProductPageComponentsQueryKey
>
export function findAllProductPageComponentsQueryOptions<
  TData = FindAllProductPageComponents["response"],
  TQueryData = FindAllProductPageComponents["response"],
>(
  params: FindAllProductPageComponents["queryParams"],
  options: FindAllProductPageComponents["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindAllProductPageComponents["response"],
    FindAllProductPageComponents["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findAllProductPageComponentsQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindAllProductPageComponents["data"],
        FindAllProductPageComponents["error"]
      >({
        method: "get",
        url: `/v1/product-page-components`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find all product page components
 * @link /v1/product-page-components
 */
export function useFindAllProductPageComponents<
  TData = FindAllProductPageComponents["response"],
  TQueryData = FindAllProductPageComponents["response"],
  TQueryKey extends QueryKey = FindAllProductPageComponentsQueryKey,
>(
  params: FindAllProductPageComponents["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindAllProductPageComponents["response"],
        FindAllProductPageComponents["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindAllProductPageComponents["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindAllProductPageComponents["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findAllProductPageComponentsQueryKey(params)
  const query = useQuery<
    FindAllProductPageComponents["data"],
    FindAllProductPageComponents["error"],
    TData,
    any
  >({
    ...findAllProductPageComponentsQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindAllProductPageComponents["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
